import { ChannelName } from "./ui/channel.tsx";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, useState } from "react";
import { api, q } from "./api.ts";
import { useRefresh } from "./components.tsx";
import { communityLabel, PROFILE_DESIGN as D, PROFILE_NAV_LABELS as N, PRODUCT_SOURCE_LABELS as L, reasonText } from "./labels.ts";
import { Button, ErrorText, Switch } from "./ui.tsx";
import { Skeleton } from "./ui/skeleton.tsx";

// What zooi watches for a product's discussions: search queries, communities
// and alternatives, opened in place under the discussion source in Insights ›
// Data sources (docs/design-system.md §0.4f, §0.3d). Each item is one line with
// its switch; what a kind is for shows only while it is empty.

/** The three kinds, in the order they are shown. */
export const WATCH_KINDS = [
  { kind: "query", title: () => N.watchingQueries, note: () => N.watchingQueriesNote, placeholder: () => L.watchQueryPlaceholder },
  { kind: "community", title: () => N.communities, note: () => N.communitiesNote, placeholder: () => N.communitiesPlaceholder },
  { kind: "competitor", title: () => N.alternatives, note: () => N.alternativesNote, placeholder: () => N.alternativesPlaceholder },
] as const;

/** One kind of watch item: each with its switch and why, and a field to add one. */
export function WatchList({ productId, kind, items, title, note, placeholder }: { productId: string; kind: C.WatchItem["kind"]; items: C.WatchItem[]; title: string; note: string; placeholder: string }) {
  const refresh = useRefresh(productId);
  const [value, setValue] = useState("");
  const add = useMutation({
    mutationFn: () => api.post(`/api/products/${productId}/watch`, { kind, value }),
    onSuccess: async () => {
      setValue("");
      await refresh();
    },
  });
  const toggle = useMutation({ mutationFn: (w: C.WatchItem) => api.patch(`/api/products/${productId}/watch/${w.id}`, { enabled: !w.enabled }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (w: C.WatchItem) => api.del(`/api/products/${productId}/watch/${w.id}`), onSuccess: refresh });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  const shown = (w: C.WatchItem) => kind === "community" ? communityLabel(w.value) : w.value;
  return (
    <section className="z-watch" aria-label={title}>
      <h3>{title} <span className="z-count">{items.filter((i) => i.enabled).length}</span></h3>
      {items.length ? (
        <ul className="z-watch-items">
          {items.map((w) => (
            <li key={w.id} className="z-watch-item" title={[w.why && reasonText(w.why), kind === "community" && w.meta.scannable === false ? D.notSearchable : null].filter(Boolean).join(" · ") || undefined}>
              <Switch checked={w.enabled} onChange={() => toggle.mutate(w)} label={D.watchEnabled(shown(w))} />
              <span className={w.enabled ? "z-watch-value" : "z-watch-value z-muted"}>{kind === "community" ? <ChannelName channel={w.value}>{shown(w)}</ChannelName> : shown(w)}</span>
              {kind === "community" && w.meta.scannable === false && <span className="z-small z-muted">{D.notSearchableShort}</span>}
              <Button size="sm" tone="ghost" className="z-reveal" aria-label={D.watchRemove(w.value)} title={D.watchRemove(w.value)} onClick={() => remove.mutate(w)}>
                <TrashIcon size={14} />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="z-small z-muted">{note}</p>
      )}
      <form onSubmit={submit} className="z-watch-add">
        <input className="z-input" aria-label={`${D.watchValue} · ${title}`} placeholder={placeholder} value={value} onChange={(e) => setValue(e.target.value)} />
        <Button size="sm" type="submit" aria-label={`${D.watchAdd} · ${title}`} title={D.watchAdd} busy={add.isPending} disabled={value.trim().length < 2}>
          <PlusIcon size={14} />
        </Button>
      </form>
      <ErrorText error={add.error ?? toggle.error ?? remove.error} />
    </section>
  );
}

/** A product's watch lists, one after another. */
export function WatchLists({ productId }: { productId: string }) {
  const product = useQuery(q.product(productId));
  if (product.isLoading) return <Skeleton shape="list" rows={2} />;
  if (!product.data) return <ErrorText error={product.error} />;
  const watch = product.data.watch;
  return <>{WATCH_KINDS.map((w) => <WatchList key={w.kind} productId={productId} kind={w.kind} items={watch.filter((item) => item.kind === w.kind)} title={w.title()} note={w.note()} placeholder={w.placeholder()} />)}</>;
}
